import type { RootStoreOptions } from "@intrasync/stores";

type RouterStoreProps = NonNullable<RootStoreOptions["routerStoreProps"]>;
import { LayoutType } from "types/LayoutType";
import { lazy } from "react";
import routesListArray from "./views/routesList";
import serviceStores from "serviceStoresConfig";
import appStores from "appStoresConfig";
import { env } from "./config/environment";

const routesList = routesListArray.reduce(
  (acc: { [key: string]: typeof route }, route) => {
    if (route.name) {
      acc[route.name] = route;
    }
    return acc;
  },
  {}
);

const LazyApp = lazy(() =>
  import("@intrasync/react").then((module) => ({
    default: module.AppLayout,
  }))
);

const LazyAuth = lazy(() =>
  import("@intrasync/react").then((module) => ({
    default: module.AuthLayout,
  }))
);

const layoutsListArray: LayoutType[] = [
  {
    name: "App",
    path: "/app",
    component: LazyApp,
    requiresAuth: false,
  },
  { name: "Auth", path: "/auth", component: LazyAuth, requiresAuth: false },
  { name: "Admin", path: "/admin", component: LazyApp, requiresAuth: true },
];

const layoutsList = layoutsListArray.reduce(
  (acc: { [key: string]: LayoutType }, layout) => {
    if (layout.name) {
      acc[layout.name] = layout;
    }
    return acc;
  },
  {}
);

const rootStoreConfig: RootStoreOptions = {
  appName: env.appName,
  // storage: localforage,
  storage: localStorage,
  mode: env.isProduction
    ? "production"
    : env.isDevelopment
    ? "development"
    : "test",
  appVersion: env.appVersion,
  appTitle: "IntraSync",
  asyncStorage: true,
  autoUpdate: env.enableAutoUpdate,
  enableNotificationStore: true,
  enableRequestQueue: true,
  enableWebApiStore: true,
  enableBarcodeCameraStore: true,
  enableDataGridStore: true,
  enableScheduleGridStore: true,
  viewStoreProps: { enableNotifications: true },
  syncChannels: true,
  storageName: env.appName,
  apiOptions: {
    debugMode: env.enableDebugMode,
    uriOverride: {
      development: env.apiUrl,
      production: env.apiUrl,
      test: env.apiUrl,
    },
    enableFindOne: true,
    enableSocketAuth: env.enableSocketAuth,
  },
  enableAuthStore: { debugMode: env.enableDebugMode },
  channelStoreDebugMode: env.enableDebugMode,
  disableLogsStore: env.isProduction && !env.enableDebugMode,
  disableViewStore: false,
  enableRouterStore: true,
  routerStoreProps: {
    defaultRoute: "Home",
    debugMode: env.enableDebugMode,
    routes: routesList,
    layouts: layoutsList,
    historyEnabled: true,
  } as unknown as RouterStoreProps,
  appStores: appStores,
  serviceStores: serviceStores,
  // `app` is injected by createRootStore (built via the app-local apiClient);
  // the published @intrasync/stores RootStore requires it via props.app.
  appLayoutStore: {
    toolboxBase: "job",
    rightNavButtonTitle: "Client",
    rightNavDefaultRoute: "ProjectDashboard",
  },
  enableFindAllStore: true,
};

export default rootStoreConfig;
